// shadcn/ui component: https://ui.shadcn.com/docs/components/radix/sidebar
import React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from 'src/component/ds/utils';
import { Skeleton } from 'src/component/ds/ui/skeleton';

/*
 * The menu parts of shadcn's Sidebar. Kept to the shadcn source so the rest
 * (SidebarProvider, Sidebar, Trigger, Rail) can be added to this file as is;
 * style a particular use from its own components. Local changes:
 * - Not yet ported: SidebarProvider and the parts that need it, so there's no
 *   `tooltip` prop and no `group-data-[collapsible=icon]` classes (the whole
 *   sidebar shrinking to an icon rail, not a row's expand chevron).
 * - Focus uses the DS focus shadow, not `ring-2`.
 */

const SidebarMenu = React.forwardRef<HTMLUListElement, React.ComponentProps<'ul'>>(
  ({ className, ...props }, ref) => (
    <ul
      ref={ref}
      data-sidebar='menu'
      className={cn('ds-flex ds-w-full ds-min-w-0 ds-flex-col ds-gap-1', className)}
      {...props}
    />
  )
);
SidebarMenu.displayName = 'SidebarMenu';

const SidebarMenuItem = React.forwardRef<HTMLLIElement, React.ComponentProps<'li'>>(
  ({ className, ...props }, ref) => (
    <li
      ref={ref}
      data-sidebar='menu-item'
      className={cn('ds-group/menu-item ds-relative', className)}
      {...props}
    />
  )
);
SidebarMenuItem.displayName = 'SidebarMenuItem';

const sidebarMenuButtonVariants = cva(
  [
    'ds-peer/menu-button ds-flex ds-w-full ds-items-center ds-gap-2 ds-overflow-hidden ds-rounded ds-p-2',
    'ds-text-left ds-text-sm ds-outline-none ds-transition-[width,height,padding] focus-visible:ds-shadow-chemelex-focus',
    'hover:ds-bg-sidebar-accent hover:ds-text-sidebar-accent-foreground',
    'active:ds-bg-sidebar-accent active:ds-text-sidebar-accent-foreground',
    'disabled:ds-pointer-events-none disabled:ds-opacity-50',
    'group-has-[[data-sidebar=menu-action]]/menu-item:ds-pr-8',
    'aria-disabled:ds-pointer-events-none aria-disabled:ds-opacity-50',
    'data-[active=true]:ds-bg-sidebar-accent data-[active=true]:ds-font-medium',
    'data-[active=true]:ds-text-sidebar-accent-foreground',
    'data-[state=open]:hover:ds-bg-sidebar-accent data-[state=open]:hover:ds-text-sidebar-accent-foreground',
    '[&>span:last-child]:ds-truncate [&>svg]:ds-size-4 [&>svg]:ds-shrink-0'
  ],
  {
    variants: {
      variant: {
        default: 'hover:ds-bg-sidebar-accent hover:ds-text-sidebar-accent-foreground',
        outline: [
          'ds-bg-background ds-shadow-[0_0_0_1px_var(--ds-sidebar-border)]',
          'hover:ds-bg-sidebar-accent hover:ds-text-sidebar-accent-foreground',
          'hover:ds-shadow-[0_0_0_1px_var(--ds-sidebar-accent)]'
        ]
      },
      size: {
        default: 'ds-h-8 ds-text-sm',
        sm: 'ds-h-7 ds-text-xs',
        lg: 'ds-h-12 ds-text-sm'
      }
    },
    defaultVariants: { variant: 'default', size: 'default' }
  }
);

const SidebarMenuButton = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<'button'> & {
    asChild?: boolean;
    isActive?: boolean;
  } & VariantProps<typeof sidebarMenuButtonVariants>
>(({ asChild = false, isActive = false, variant = 'default', size = 'default', className, ...props }, ref) => {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      ref={ref}
      data-sidebar='menu-button'
      data-size={size}
      data-active={isActive}
      className={cn(sidebarMenuButtonVariants({ variant, size }), className)}
      {...props}
    />
  );
});
SidebarMenuButton.displayName = 'SidebarMenuButton';

const SidebarMenuAction = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<'button'> & {
    asChild?: boolean;
    showOnHover?: boolean;
  }
>(({ className, asChild = false, showOnHover = false, ...props }, ref) => {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      ref={ref}
      data-sidebar='menu-action'
      className={cn(
        'ds-absolute ds-right-1 ds-top-1.5 ds-flex ds-aspect-square ds-w-5 ds-items-center ds-justify-center',
        'ds-rounded ds-p-0 ds-text-sidebar-foreground ds-outline-none ds-transition-transform',
        'hover:ds-bg-sidebar-accent hover:ds-text-sidebar-accent-foreground focus-visible:ds-shadow-chemelex-focus',
        'peer-hover/menu-button:ds-text-sidebar-accent-foreground [&>svg]:ds-size-4 [&>svg]:ds-shrink-0',
        // Increases the hit area of the button on mobile.
        'after:ds-absolute after:-ds-inset-2 md:after:ds-hidden',
        'peer-data-[size=sm]/menu-button:ds-top-1',
        'peer-data-[size=default]/menu-button:ds-top-1.5',
        'peer-data-[size=lg]/menu-button:ds-top-2.5',
        showOnHover && [
          'group-focus-within/menu-item:ds-opacity-100 group-hover/menu-item:ds-opacity-100',
          'data-[state=open]:ds-opacity-100 peer-data-[active=true]/menu-button:ds-text-sidebar-accent-foreground',
          'md:ds-opacity-0'
        ],
        className
      )}
      {...props}
    />
  );
});
SidebarMenuAction.displayName = 'SidebarMenuAction';

const SidebarMenuBadge = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-sidebar='menu-badge'
      className={cn(
        'ds-pointer-events-none ds-absolute ds-right-1 ds-flex ds-h-5 ds-min-w-5 ds-select-none ds-items-center',
        'ds-justify-center ds-rounded ds-px-1 ds-text-xs ds-font-medium ds-tabular-nums ds-text-sidebar-foreground',
        'peer-hover/menu-button:ds-text-sidebar-accent-foreground',
        'peer-data-[active=true]/menu-button:ds-text-sidebar-accent-foreground',
        'peer-data-[size=sm]/menu-button:ds-top-1',
        'peer-data-[size=default]/menu-button:ds-top-1.5',
        'peer-data-[size=lg]/menu-button:ds-top-2.5',
        className
      )}
      {...props}
    />
  )
);
SidebarMenuBadge.displayName = 'SidebarMenuBadge';

const SidebarMenuSkeleton = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'> & { showIcon?: boolean }>(
  ({ className, showIcon = false, ...props }, ref) => {
    // Random width between 50 to 90%.
    const width = React.useMemo(() => `${Math.floor(Math.random() * 40) + 50}%`, []);

    return (
      <div
        ref={ref}
        data-sidebar='menu-skeleton'
        className={cn('ds-flex ds-h-8 ds-items-center ds-gap-2 ds-rounded ds-px-2', className)}
        {...props}
      >
        {showIcon && <Skeleton className='ds-size-4 ds-rounded' data-sidebar='menu-skeleton-icon' />}
        <Skeleton
          className='ds-h-4 ds-max-w-[var(--skeleton-width)] ds-flex-1'
          data-sidebar='menu-skeleton-text'
          style={{ '--skeleton-width': width } as React.CSSProperties}
        />
      </div>
    );
  }
);
SidebarMenuSkeleton.displayName = 'SidebarMenuSkeleton';

const SidebarMenuSub = React.forwardRef<HTMLUListElement, React.ComponentProps<'ul'>>(
  ({ className, ...props }, ref) => (
    <ul
      ref={ref}
      data-sidebar='menu-sub'
      className={cn(
        'ds-mx-3.5 ds-flex ds-min-w-0 ds-translate-x-px ds-flex-col ds-gap-1',
        'ds-border-l ds-border-sidebar-border ds-px-2.5 ds-py-0.5',
        className
      )}
      {...props}
    />
  )
);
SidebarMenuSub.displayName = 'SidebarMenuSub';

const SidebarMenuSubItem = React.forwardRef<HTMLLIElement, React.ComponentProps<'li'>>(
  ({ ...props }, ref) => <li ref={ref} {...props} />
);
SidebarMenuSubItem.displayName = 'SidebarMenuSubItem';

const SidebarMenuSubButton = React.forwardRef<
  HTMLAnchorElement,
  React.ComponentPropsWithoutRef<'a'> & {
    asChild?: boolean;
    size?: 'sm' | 'md';
    isActive?: boolean;
  }
>(({ asChild = false, size = 'md', isActive, className, ...props }, ref) => {
  const Comp = asChild ? Slot : 'a';

  return (
    <Comp
      ref={ref}
      data-sidebar='menu-sub-button'
      data-size={size}
      data-active={isActive}
      className={cn(
        'ds-flex ds-h-7 ds-min-w-0 -ds-translate-x-px ds-items-center ds-gap-2 ds-overflow-hidden ds-rounded ds-px-2',
        'ds-text-sidebar-foreground ds-outline-none focus-visible:ds-shadow-chemelex-focus',
        'hover:ds-bg-sidebar-accent hover:ds-text-sidebar-accent-foreground',
        'active:ds-bg-sidebar-accent active:ds-text-sidebar-accent-foreground',
        'disabled:ds-pointer-events-none disabled:ds-opacity-50',
        'aria-disabled:ds-pointer-events-none aria-disabled:ds-opacity-50',
        '[&>span:last-child]:ds-truncate [&>svg]:ds-size-4 [&>svg]:ds-shrink-0 [&>svg]:ds-text-sidebar-accent-foreground',
        'data-[active=true]:ds-bg-sidebar-accent data-[active=true]:ds-text-sidebar-accent-foreground',
        size === 'sm' && 'ds-text-xs',
        size === 'md' && 'ds-text-sm',
        className
      )}
      {...props}
    />
  );
});
SidebarMenuSubButton.displayName = 'SidebarMenuSubButton';

export {
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  sidebarMenuButtonVariants
};
